import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Rocket } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import {
  fetchGroupBoosts,
  fetchGroupBoosters,
  type AdminBooster,
  type AdminGroupBoosts,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { Skeleton } from '@/components/States'

/**
 * A4.19 — Boost holati. GET /admin/groups/:id/boosts (backend `46dc8f2`).
 * MyGram boostlari BEPUL (pul yo'q) — daraja booster soniga qarab.
 */
export function GroupBoosts() {
  const t = useT()
  const { id = '' } = useParams()
  const [summary, setSummary] = useState<AdminGroupBoosts | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    fetchGroupBoosts(Number(id), ac.signal)
      .then((s) => {
        if (alive) setSummary(s)
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id])

  const columns = useMemo<AdminColumn<AdminBooster>[]>(
    () => [
      {
        key: 'user_id',
        header: t.col.name,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.first_name || (r.username ? `@${r.username}` : `#${r.user_id}`)}
          </Link>
        ),
      },
      { key: 'username', header: t.col.username, type: 'text', width: 160 },
      { key: 'created_at', header: t.groups.boostedAt, type: 'date', width: 170 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupBoosters(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.boosts}
        screenId="A4.19"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <div className="flex items-center gap-3 border-b border-border-subtle p-3">
        <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-2.5">
          <Rocket size={18} className="text-accent" />
          {loading ? (
            <Skeleton className="h-6 w-28" />
          ) : (
            <div className="flex gap-6">
              <div>
                <div className="text-xs text-tertiary">{t.groups.boostLevel}</div>
                <div className="font-mono text-xl tabular text-accent">{summary?.level ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-tertiary">{t.groups.boostCount}</div>
                <div className="font-mono text-xl tabular text-primary">{summary?.count ?? 0}</div>
              </div>
            </div>
          )}
        </div>
        <span className="text-xs text-tertiary">{t.groups.boostFree}</span>
      </div>
      <DataTable<AdminBooster>
        screenId="A4.19"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.user_id)}
        exportable
        emptyState={{ icon: <Rocket size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
